import type { ClueOut, ClueSource, CustomerProposalOut, ProposalFieldsOut } from "@/api";

import type { CustomerDraft } from "./CustomerForm";

/**
 * Where a value stands in the mail, in words (spec 2026-10-20 §3.1) — never a
 * percentage, never a confidence (AN-14): a person checks a value against
 * the line it came from, not against a number about it.
 */
export const SOURCE_WORDS: Record<ClueSource, string> = {
  sender: "aus dem Absender",
  reply_to: "aus Reply-To",
  recipient: "aus dem Empfänger",
  signature: "aus der Signatur",
  form: "aus dem Formular",
  body: "aus dem Text",
  subject: "aus dem Betreff",
};

/** The customer fields as the form edits them, and the clue each one starts from. */
const DRAFT_FIELDS: readonly (readonly [keyof CustomerDraft, keyof ProposalFieldsOut])[] = [
  ["displayName", "name"],
  ["company", "company"],
  ["email", "email"],
  ["phone", "phone"],
  ["mobile", "mobile"],
  ["street", "street"],
  ["postalCode", "postalCode"],
  ["city", "city"],
  ["customerNo", "customerNo"],
];

/** What the mail gives for a new customer — a field it does not give stays out. */
export function draftFromMail(fields: ProposalFieldsOut): Partial<CustomerDraft> {
  const draft: Partial<CustomerDraft> = {};
  for (const [key, clue] of DRAFT_FIELDS) {
    const value = fields[clue]?.value;
    if (value !== undefined) draft[key] = value;
  }
  return draft;
}

/** Where each of those values stands, in words. */
export function sourcesFromMail(fields: ProposalFieldsOut): Partial<Record<keyof CustomerDraft, string>> {
  const sources: Partial<Record<keyof CustomerDraft, string>> = {};
  for (const [key, clue] of DRAFT_FIELDS) {
    const source = fields[clue]?.source;
    if (source !== undefined) sources[key] = SOURCE_WORDS[source];
  }
  return sources;
}

function clues(fields: ProposalFieldsOut): ClueOut[] {
  return Object.values(fields).filter((clue): clue is ClueOut => clue !== null);
}

/**
 * The lines of the mail's text a value stems from, right-trimmed as the
 * reader keeps them. A value read across lines (a wrapped header, a form
 * value under its label) joins them with a line break: each counts.
 */
export function clueLines(fields: ProposalFieldsOut): ReadonlySet<string> {
  const lines = new Set<string>();
  for (const clue of clues(fields)) {
    for (const line of clue.line.split("\n")) {
      const trimmed = line.trimEnd();
      if (trimmed.trim() !== "") lines.add(trimmed);
    }
  }
  return lines;
}

/** Whether any value was read from this place — the subject, the sender's header. */
export function readFrom(fields: ProposalFieldsOut, source: ClueSource): boolean {
  return clues(fields).some((clue) => clue.source === source);
}

/**
 * The contact person as the rules read them (spec D-8): who wrote, how to
 * reach them. The telephone is the landline where there is one, else the
 * mobile — one number, the one to call back.
 *
 * **Only from `fields`, never from the mail's outer header** (Task 10 review,
 * I-2): the reader leaves a field empty exactly where the outer sender is
 * K60 or the person who filed the mail — a forward. `mail.fromName` would
 * then be the salesperson's own name, saved as the contact and printed on
 * the cover sheet.
 */
export function contactFromMail(proposal: CustomerProposalOut | undefined): ContactDraft {
  const fields = proposal?.readable === true ? proposal.fields : null;
  return {
    name: fields?.name?.value ?? "",
    email: fields?.email?.value ?? "",
    phone: fields?.phone?.value ?? fields?.mobile?.value ?? "",
  };
}

/** The three contact fields as the screen edits them — blank until typed. */
export interface ContactDraft {
  name: string;
  email: string;
  phone: string;
}
